/**
 * The people field — *Assigned to* (a task), *Who's going* (a meeting),
 * *Members* (a task list) — `docs/activity.md` §9.5, §9.6, §4.7, Boards 11
 * E–F, 16 E, 19 C, 20 A.
 *
 * A chip per person (22 px initials, the name, ×; the viewer first, *Me* on a
 * phone), **+ Add a person**, and the words at the right. **+ Add a person**
 * offers the people the viewer may book; anybody else is listed with a lock
 * and is not pickable (§5.3). Up to 25 (D21).
 */
import { useMemo, useRef, useState } from 'react'
import { MAX_PARTICIPANTS, TOO_MANY_PEOPLE, firstNameOf, type ActivityPerson } from '@rowted/shared'
import { Popover } from '../../components/overlays.js'
import { PanelSheet } from '../../components/panel-sheet.js'
import { BackLayer } from '../use-activity-history.js'
import { cn } from '../../lib/cn.js'
import { Face } from '../faces.js'
import { Glyph } from '../glyphs.js'
import { useActivity } from '../activity-context.js'
import { FOCUS, PICKER_LAYER } from './kit.js'

export interface PersonChip { id: string; name: string; initials: string; colour: string | null }

export function usePeopleLookup(): {
  bookable: ActivityPerson[]
  locked: { id: string; name: string; initials: string; place: string }[]
  byId: (id: string) => PersonChip | null
} {
  const { directory, me } = useActivity()
  return useMemo(() => {
    const people = directory?.people ?? []
    const bookable = people.filter((person) => person.book || person.id === me.user.id)
    const locked = [
      ...people.filter((person) => !person.book && person.id !== me.user.id)
        .map((person) => ({ id: person.id, name: person.name, initials: person.initials, place: person.place })),
      ...(directory?.locked ?? []).map((person) => ({ id: person.id, name: person.name, initials: person.initials, place: person.place })),
    ]
    const byId = (id: string): PersonChip | null => {
      const found = people.find((person) => person.id === id)
      return found ? { id: found.id, name: found.name, initials: found.initials, colour: found.colour } : null
    }
    return { bookable, locked, byId }
  }, [directory, me.user.id])
}

function PersonPicker({
  chosen, onPick, phone, onClose,
}: { chosen: string[]; onPick: (id: string) => void; phone: boolean; onClose: () => void }) {
  const { bookable, locked } = usePeopleLookup()
  const [q, setQ] = useState('')
  const wanted = q.trim().toLowerCase()
  const shown = bookable.filter((person) => !chosen.includes(person.id) && (!wanted || person.name.toLowerCase().includes(wanted)))
  const shut = locked.filter((person) => !wanted || person.name.toLowerCase().includes(wanted))
  const row = phone ? 'min-h-[52px] px-4 text-[15px]' : 'px-3 py-[7px] text-[13px]'
  return (
    <div className="flex min-h-0 flex-col">
      <div className={phone ? 'px-4 pb-1 pt-2.5' : 'px-2.5 pb-1 pt-2'}>
        <label className={cn('flex items-center gap-2 rounded-lg border border-line px-2.5', FOCUS,
          phone ? 'h-[46px] text-[15px]' : 'h-9 text-[13px]')}>
          <span className="text-text-muted"><Glyph name="search" size={15} /></span>
          <input autoFocus value={q} onChange={(event) => setQ(event.target.value)} aria-label="Find a person"
            placeholder="Find a person"
            onKeyDown={(event) => {
              if (event.key === 'Enter' && shown[0]) { event.preventDefault(); onPick(shown[0].id) }
              if (event.key === 'Escape') { event.stopPropagation(); onClose() }
            }}
            className="min-w-0 flex-1 bg-transparent outline-none focus-visible:shadow-none placeholder:text-text-faint" />
        </label>
      </div>
      <div role="listbox" aria-label="People" className="flex min-h-0 flex-col overflow-y-auto py-1">
        {shown.map((person) => (
          <button key={person.id} type="button" role="option" aria-selected={false} onClick={() => onPick(person.id)}
            className={cn('flex w-full items-center gap-2.5 text-left hover:bg-hover', row)}>
            <Face person={person} size={22} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-semibold">{person.name}</span>
              <span className={cn('truncate text-text-muted', phone ? 'text-[12.5px]' : 'text-[11.5px]')}>
                {person.role} · {person.workDays}
              </span>
            </span>
          </button>
        ))}
        {shut.map((person) => (
          <div key={person.id} aria-disabled className={cn('flex w-full items-center gap-2.5 opacity-60', row)}>
            <span className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full bg-line-soft text-text-muted">
              <Glyph name="lock" size={12} />
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-semibold">{person.name}</span>
              <span className={cn('truncate text-text-muted', phone ? 'text-[12.5px]' : 'text-[11.5px]')}>
                {person.place} — ask an admin to book for {firstNameOf(person.name)}
              </span>
            </span>
          </div>
        ))}
        {shown.length === 0 && shut.length === 0 && (
          <div className={cn('text-text-muted', row)}>Nobody else to add.</div>
        )}
      </div>
    </div>
  )
}

export function PeopleField({
  label, required, people, onChange, words, phone, meWord = false, max = MAX_PARTICIPANTS, wordsWhenOne,
}: {
  label: string
  required?: boolean
  people: string[]
  onChange: (people: string[]) => void
  /** The right-hand words — *one person or several — one tick finishes it for all*, *free then*, *up to 25*. */
  words?: string
  phone: boolean
  /** The viewer's own chip reads *Me* (Board 16 E). */
  meWord?: boolean
  max?: number
  /** Board 16 E draws no words on a phone. */
  wordsWhenOne?: boolean
}) {
  const { me } = useActivity()
  const { byId } = usePeopleLookup()
  const [open, setOpen] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const box = useRef<HTMLDivElement>(null)
  void wordsWhenOne

  const add = (id: string) => {
    if (people.length >= max) { setProblem(TOO_MANY_PEOPLE); setOpen(false); return }
    setProblem(null)
    onChange([...people, id])
    setOpen(false)
  }

  return (
    <div className="min-w-0">
      <div className="mb-[5px] flex items-center gap-1 text-[11.5px] font-medium text-text-muted">
        <span>{label}</span>
        {required && <i aria-hidden className="inline-block h-[5px] w-[5px] rounded-full bg-accent" />}
      </div>
      <div ref={box} role="group" aria-label={label}
        className={cn('flex flex-wrap items-center gap-1.5 border border-line bg-surface',
          phone ? 'rounded-[10px] p-1.5' : 'min-h-[38px] rounded-lg px-1.5 py-1')}>
        {people.map((id) => {
          const person = byId(id) ?? (id === me.user.id
            ? { id, name: me.user.name, initials: me.user.name.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase(), colour: null }
            : null)
          if (!person) return null
          return (
            <span key={id} className={cn('inline-flex items-center whitespace-nowrap rounded-full border border-line bg-surface font-semibold',
              phone ? 'h-8 gap-1.5 pl-1 pr-2 text-[14px]' : 'h-7 gap-1.5 pl-[3px] pr-1.5 text-[12px]')}>
              <Face person={person} size={phone ? 24 : 22} />
              {meWord && id === me.user.id ? 'Me' : person.name}
              <button type="button" aria-label={`Remove ${person.name}`}
                onClick={() => { setProblem(null); onChange(people.filter((one) => one !== id)) }}
                className="inline-flex text-text-muted hover:text-text">
                <Glyph name="close" size={phone ? 14 : 12} />
              </button>
            </span>
          )
        })}
        <button type="button" onClick={() => setOpen(true)} aria-haspopup="dialog"
          className={cn('inline-flex items-center gap-1 font-semibold text-accent',
            phone ? 'px-1.5 text-[14px]' : 'h-7 px-1.5 text-[12px]')}>
          <Glyph name="plus" size={phone ? 15 : 13} />Add a person
        </button>
        {words && <span className="ml-auto pr-1 text-[11px] text-text-faint">{words}</span>}
      </div>
      {problem && <div role="alert" className="mt-1 text-[11.5px] text-danger">{problem}</div>}
      {/* Back closes the picker first (Aaron, Oct 26). */}
      {phone && open && <BackLayer onBack={() => setOpen(false)} />}
      {phone ? (
        <PanelSheet open={open} onClose={() => setOpen(false)} title="Add a person" layer={PICKER_LAYER}>
          {open && <PersonPicker phone chosen={people} onPick={add} onClose={() => setOpen(false)} />}
        </PanelSheet>
      ) : (
        <Popover open={open} onClose={() => setOpen(false)} anchor={box.current} label="Add a person" width={340}
          layer={PICKER_LAYER} maxHeight={380} className="py-1">
          {open && <PersonPicker phone={false} chosen={people} onPick={add} onClose={() => setOpen(false)} />}
        </Popover>
      )}
    </div>
  )
}
